import React, { useState, useEffect } from 'react'
import styled from '@emotion/styled'
import { getGrooverText } from '../../libs/GrooverTextRepository'
import useAppState from '../../hooks/useAppState'
import GrooverText from '../GrooverText'

interface VerificationCodeFormProps {
  phoneNumber: string
  onSubmit: (code: string) => Promise<void>
  onResend: () => Promise<void>
}

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 24px;
  width: 100%;
  max-width: 400px;
`

const Title = styled.h1`
  font-size: 28px;
  font-weight: 600;
  color: #ffffff;
  margin: 0;
  text-align: center;
`

const Description = styled.p`
  font-size: 16px;
  color: #a0a0a0;
  margin: 0;
  text-align: center;
`

const InputWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const CodeInput = styled.input`
  padding: 14px 16px;
  font-size: 24px;
  font-weight: 600;
  letter-spacing: 8px;
  text-align: center;
  border: 2px solid #35365a;
  border-radius: 8px;
  background: #1e2028;
  color: #ffffff;
  transition: border-color 0.2s;

  &:focus {
    outline: none;
    border-color: var(--portal-primary, #7c3aed);
  }

  &::placeholder {
    color: #6b6b6b;
    letter-spacing: normal;
  }

  &.error {
    border-color: #ef4444;
  }
`

const SubmitButton = styled.button`
  padding: 14px 24px;
  font-size: 16px;
  font-weight: 600;
  color: #ffffff;
  background: var(--portal-primary, #7c3aed);
  border: none;
  border-radius: 8px;
  cursor: pointer;
  transition: background 0.2s;

  &:hover:not(:disabled) {
    background: var(--portal-primary-dark, #6d28d9);
  }

  &:disabled {
    background: #4a4a4a;
    cursor: not-allowed;
  }
`

const ResendButton = styled.button`
  padding: 12px 24px;
  font-size: 14px;
  font-weight: 500;
  color: var(--portal-primary, #7c3aed);
  background: transparent;
  border: 1px solid var(--portal-primary, #7c3aed);
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s;

  &:hover:not(:disabled) {
    background: rgba(var(--portal-primary-rgb, 124, 58, 237), 0.1);
  }

  &:disabled {
    color: #6b6b6b;
    border-color: #4a4a4a;
    cursor: not-allowed;
  }
`

const ErrorText = styled.p`
  font-size: 14px;
  color: #ef4444;
  margin: 0;
`

export const VerificationCodeForm: React.FC<VerificationCodeFormProps> = ({ phoneNumber, onSubmit, onResend }) => {
  const [code, setCode] = useState('')
  const [error, setError] = useState<string>()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [canResend, setCanResend] = useState(false)
  const [countdown, setCountdown] = useState(60)
  const { state } = useAppState()
  const selectedLanguage = state.selectedLanguage

  // Countdown timer for resend button
  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000)
      return () => clearTimeout(timer)
    } else {
      setCanResend(true)
    }
  }, [countdown])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (code.length !== 6) {
      setError(getGrooverText('portalLoginCodeInvalid', selectedLanguage))
      return
    }

    setIsSubmitting(true)
    setError(undefined)

    try {
      await onSubmit(code)
    } catch {
      setError(getGrooverText('portalLoginCodeError', selectedLanguage))
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleResend = async () => {
    setCanResend(false)
    setCountdown(60)
    await onResend()
  }

  const handleCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\D/g, '').slice(0, 6)
    setCode(value)
  }

  return (
    <Form onSubmit={handleSubmit}>
      <Title>
        <GrooverText textKey="portalLoginCodeTitle" />
      </Title>
      <Description>
        {getGrooverText('portalLoginCodeDescription', selectedLanguage).replace('{0}', phoneNumber)}
      </Description>

      <InputWrapper>
        <CodeInput
          type="text"
          inputMode="numeric"
          value={code}
          onChange={handleCodeChange}
          maxLength={6}
          placeholder="000000"
          autoFocus
          className={error ? 'error' : ''}
          disabled={isSubmitting}
        />
        {error && <ErrorText>{error}</ErrorText>}
      </InputWrapper>

      <SubmitButton type="submit" disabled={isSubmitting || code.length !== 6}>
        {isSubmitting ? <GrooverText textKey="portalLoginVerifying" /> : <GrooverText textKey="portalLoginVerify" />}
      </SubmitButton>

      <ResendButton type="button" onClick={handleResend} disabled={!canResend}>
        {canResend ? (
          <GrooverText textKey="portalLoginResendCode" />
        ) : (
          getGrooverText('portalLoginResendCodeCountdown', selectedLanguage).replace('{0}', countdown.toString())
        )}
      </ResendButton>
    </Form>
  )
}
